import * as React from 'react';
import { IReleaseStep, WelcomePackDto, WelcomePackStatus, WelcomePackStepType } from 'dto/welcome-pack.dto';
import { CheckboxField, showConfirmDialog, Typography } from 'ui-lib';
import { PSP } from 'dto/key-management/key-dto';
import { useAccountTreeSelector } from 'hooks/accounts/account-datasource.hook';
import { AccountType, IAccount } from 'dto/access-management/account-dto';
import { useStores } from 'store';
import { useLoadingStates } from 'hooks/use-loading.hook';
import { useUrlSearch } from 'hooks/use-search.hook';
import { useAlertMessages } from 'pages/private/components/alert/use-messages';
import { useNavigate } from 'react-router-dom';
import { buildNavigatePath } from 'pages/private/utils';


export class WelcomePackUtils {
    public static SUPPORTED_PSP_TYPES: PSP[] = [PSP.LittlePay, PSP.Pulse, PSP.Unicard, PSP.ExternalKeyManager];

    public static isActive(welcomePack: WelcomePackDto): boolean {
        return welcomePack?.status === WelcomePackStatus.ACTIVE;
    }

    public static isPspSupported(psp: string | PSP): boolean {
        return WelcomePackUtils.SUPPORTED_PSP_TYPES.includes(psp as PSP);
    }

    public static showDeactivateConfirmDialog(action: (force?: boolean) => Promise<void>): void {
        let checked = false;
        return showConfirmDialog({
            title: 'Deactivate Welcome Pack',
            content: (
                <div>
                    <Typography.Text>Are you sure you want to deactivate the welcome pack?</Typography.Text>
                    <CheckboxField
                        label={<Typography.Text italic>Revoke processing of all devices using selected Welcome pack</Typography.Text>}
                        key='force-deactivate'
                        onChange={(e) => {
                            checked = e.target.checked;
                        }}
                    />
                </div>
            ),
            okText: 'OK',
            onOk: async () => {
                await action(checked);
            },
        });
    }

    public static showCreateConfirmDialog(action: () => Promise<void>): void {
        return showConfirmDialog({
            title: 'Create Welcome Pack',
            content: (
                <div>
                    <Typography.Text>
                        Are you sure you want to create the welcome pack with this configuration?
                    </Typography.Text>
                </div>
            ),
            okText: 'OK',
            onOk: async () => {
                await action();
            },
        });
    }
}

interface IWelcomePackStates {
    accountSelector: any;
    selectedAccount: IAccount | null;
    welcomePack?: WelcomePackDto | null;
    loading: boolean;
    isActive?: boolean;
    processingSteps?: IReleaseStep[] | null;
    failureStepConfiguration?: IReleaseStep | null;
    warningMessage?: string;
    onBackToWelcomePacks: (fn?: unknown) => void;
}
export const useWelcomePackQueryManager = (onAccountChange?: (account: IAccount) => Promise<void>): IWelcomePackStates => {
    const navigate = useNavigate();
    const { searchParams, getSearchParam, setSearchParams } = useUrlSearch();
    const { welcomePackStore, accountStore } = useStores();
    const { loading, withLoading } = useLoadingStates();
    const [welcomePack, setWelcomePack] = React.useState<WelcomePackDto>(null);
    const { clearErrorMessages, warningMessages, addWarningMessages, clearWarningMessages } = useAlertMessages();

    const accountSelector = useAccountTreeSelector({
        selectableTypes: [AccountType.CLIENT],
        defaultSelectedAccount: getSearchParam('accountUuid'),
        onAccountChange: async (account, options) => {
            if (!account) {
                return;
            }
            if (!options.isInitial) {
                setSearchParams({ accountUuid: account?.uuid });
            }
            withLoading(async () => {
                await Promise.all([
                    welcomePackStore.getWelcomePacks(account.id, account.uuid),
                    onAccountChange && onAccountChange(account),
                ]);
            });
        },
    });

    const account = React.useMemo(() => {
        const accountUuid = getSearchParam('accountUuid');
        if (!accountUuid) {
            return null;
        }
        return accountStore.getAccountByUuid(accountUuid);
    }, [searchParams]);

    React.useEffect(() => {
        if (!welcomePackStore.welcomePacks) {
            return;
        }
        withLoading(async () => {
            const welcomePackId = Number.parseInt(getSearchParam('id'));
            const activeWp = welcomePackStore.findActiveWelcomePackByAccountUuid(accountSelector.selectedAccount.uuid);
            if (!isNaN(welcomePackId)) {
                const wp = welcomePackStore.findWelcomePackByAccountUuidAndId(
                    accountSelector.selectedAccount.uuid,
                    welcomePackId,
                );
                setWelcomePack(wp);
            } else {
                setWelcomePack(activeWp);
            }
        });
    }, [searchParams, welcomePackStore.welcomePacks]);

    const isActive = React.useMemo(() => {
        clearWarningMessages();
        clearErrorMessages();
        if (!welcomePack) {
            addWarningMessages([`The account does not configure a welcome pack.`]);
            return false;
        }
        const isActive = WelcomePackUtils.isActive(welcomePack);
        if (!isActive) {
            addWarningMessages([`The welcome pack is not active.`]);
        }
        return isActive;
    }, [welcomePack]);

    return {
        accountSelector,
        selectedAccount: account,
        welcomePack,
        loading: loading || welcomePackStore.dataLoading,
        isActive,
        processingSteps:
            welcomePack?.allStepConfigurations.filter((step) => step.type !== WelcomePackStepType.FAILURE) || [],
        failureStepConfiguration: welcomePack?.allStepConfigurations.find(
            (step) => step.type === WelcomePackStepType.FAILURE,
        ),
        warningMessage: warningMessages[0],
        onBackToWelcomePacks: (fn?: unknown) => {
            fn instanceof Function && fn();
            navigate({
                pathname: buildNavigatePath('/devices/welcome-packs'),
                search: `accountUuid=${account?.uuid}`,
            });
        },
    };
};
